import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { circlePeri, percentPeri } from './doughnut-inner-utils';
import {
  CLASSROOM,
  GROUP,
  STUDENT,
  MOUSE_STATE_ENTER,
  MOUSE_STATE_LEAVE,
  MOUSE_STATE_CLICK,
  xPosCommon,
  yPosCommon,
  radiusCommon,
  colorCommon,
  widthCommon,
  fillCommon,
  dashOffsetCommon,
  lineCapCommon,
  classColor,
  groupColor,
  studentColor,
  doughnutGap
} from '../../../../../../helpers/utils';

const dashArrayCommon = circlePeri(radiusCommon);

const RawDoughnut = ({
  xPos = xPosCommon,
  yPos = yPosCommon,
  radius = radiusCommon,
  color = colorCommon,
  width = widthCommon,
  fill = fillCommon,
  dashArray = dashArrayCommon,
  dashOffset = dashOffsetCommon,
  lineCap = lineCapCommon,
  onMouseEnter = () => {},
  onMouseLeave = () => {},
  onClick = () => {},
  pointer
}) => (
  <circle
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    onClick={onClick}
    transform={`rotate(270 ${xPos} ${yPos})`}
    cx={xPos}
    cy={yPos}
    r={radius}
    stroke={color}
    strokeWidth={width}
    fill={fill}
    strokeDasharray={dashArray}
    strokeDashoffset={dashOffset}
    strokeLinecap={lineCap}
    style={{ cursor: pointer ? 'pointer' : 'default' }}
  />
);

RawDoughnut.propTypes = {
  xPos: PropTypes.number,
  yPos: PropTypes.number,
  radius: PropTypes.number,
  color: PropTypes.string,
  width: PropTypes.number,
  fill: PropTypes.string,
  dashArray: PropTypes.number,
  dashOffset: PropTypes.number,
  lineCap: PropTypes.number,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onClick: PropTypes.func,
  pointer: PropTypes.bool
};

// eslint-disable-next-line import/prefer-default-export
export const useClassroomDoughnuts = ({
  resultType = CLASSROOM,
  classroomPercent,
  groupPercent,
  studentPercent
}) => {
  const gap = resultType === STUDENT ? doughnutGap / 2 : doughnutGap;
  const groupRadius = radiusCommon - widthCommon - gap;
  const groupDashArray = circlePeri(groupRadius);

  const studentRadius = groupRadius - widthCommon - gap;
  const studentDashArray = circlePeri(studentRadius);

  const [doughnutData, setDoughnutData] = useState({
    doughnutType: CLASSROOM,
    mouseState: MOUSE_STATE_LEAVE
  });

  const ClassDoughnuts = () => (
    <>
      <RawDoughnut
        onMouseEnter={() =>
          setDoughnutData({
            doughnutType: CLASSROOM,
            mouseState: MOUSE_STATE_ENTER
          })
        }
        onMouseLeave={() =>
          setDoughnutData({
            doughnutType: CLASSROOM,
            mouseState: MOUSE_STATE_LEAVE
          })
        }
        onClick={() =>
          setDoughnutData({
            doughnutType: CLASSROOM,
            mouseState: MOUSE_STATE_CLICK
          })
        }
        pointer
      />
      {classroomPercent > 0 && (
        <RawDoughnut
          color={classColor}
          dashOffset={percentPeri(radiusCommon, classroomPercent)}
          onMouseEnter={() =>
            setDoughnutData({
              doughnutType: CLASSROOM,
              mouseState: MOUSE_STATE_ENTER
            })
          }
          onMouseLeave={() =>
            setDoughnutData({
              doughnutType: CLASSROOM,
              mouseState: MOUSE_STATE_LEAVE
            })
          }
          onClick={() =>
            setDoughnutData({
              doughnutType: CLASSROOM,
              mouseState: MOUSE_STATE_CLICK
            })
          }
          pointer
        />
      )}
    </>
  );

  const GroupDoughnuts = () => (
    <>
      <RawDoughnut
        radius={groupRadius}
        dashArray={groupDashArray}
        onMouseEnter={() =>
          setDoughnutData({
            doughnutType: GROUP,
            mouseState: MOUSE_STATE_ENTER
          })
        }
        onMouseLeave={() =>
          setDoughnutData({
            doughnutType: GROUP,
            mouseState: MOUSE_STATE_LEAVE
          })
        }
        onClick={() =>
          setDoughnutData({
            doughnutType: GROUP,
            mouseState: MOUSE_STATE_CLICK
          })
        }
        pointer
      />
      {groupPercent > 0 && (
        <RawDoughnut
          color={groupColor}
          radius={groupRadius}
          dashArray={groupDashArray}
          dashOffset={percentPeri(groupRadius, groupPercent)}
          onMouseEnter={() =>
            setDoughnutData({
              doughnutType: GROUP,
              mouseState: MOUSE_STATE_ENTER
            })
          }
          onMouseLeave={() =>
            setDoughnutData({
              doughnutType: GROUP,
              mouseState: MOUSE_STATE_LEAVE
            })
          }
          onClick={() =>
            setDoughnutData({
              doughnutType: GROUP,
              mouseState: MOUSE_STATE_CLICK
            })
          }
          pointer
        />
      )}
    </>
  );

  const StudentDoughnuts = () => (
    <>
      <RawDoughnut
        radius={studentRadius}
        dashArray={studentDashArray}
        onMouseEnter={() =>
          setDoughnutData({
            doughnutType: STUDENT,
            mouseState: MOUSE_STATE_ENTER
          })
        }
        onMouseLeave={() =>
          setDoughnutData({
            doughnutType: STUDENT,
            mouseState: MOUSE_STATE_LEAVE
          })
        }
        onClick={() =>
          setDoughnutData({
            doughnutType: STUDENT,
            mouseState: MOUSE_STATE_CLICK
          })
        }
        pointer
      />
      {studentPercent > 0 && (
        <RawDoughnut
          color={studentColor}
          radius={studentRadius}
          dashArray={studentDashArray}
          dashOffset={percentPeri(studentRadius, studentPercent)}
          onMouseEnter={() =>
            setDoughnutData({
              doughnutType: STUDENT,
              mouseState: MOUSE_STATE_ENTER
            })
          }
          onMouseLeave={() =>
            setDoughnutData({
              doughnutType: STUDENT,
              mouseState: MOUSE_STATE_LEAVE
            })
          }
          onClick={() =>
            setDoughnutData({
              doughnutType: STUDENT,
              mouseState: MOUSE_STATE_CLICK
            })
          }
          pointer
        />
      )}
    </>
  );

  const AllDoughnuts = () => {
    switch (resultType) {
      case 0:
        return <ClassDoughnuts />;
      case 1:
        return (
          <>
            <ClassDoughnuts />
            <GroupDoughnuts />
          </>
        );
      case 2:
        return (
          <>
            <ClassDoughnuts />
            <GroupDoughnuts />
            <StudentDoughnuts />
          </>
        );
      default:
        return null;
    }
  };

  return [AllDoughnuts, doughnutData];
};



// WEBPACK FOOTER //
// ./components/student-list/student-modal/student-result/doughnut-chart/doughnut-inner/doughnut-inner-hooks.js